import React from 'react';
import { Flame, PenLine, Sparkles, BookOpen, ArrowDown } from 'lucide-react';

const pillars = [
  { icon: Sparkles, title: 'A spark, every day', text: 'Six genres, six moods, one‑word sparks. You never sit down to a blank page again.' },
  { icon: PenLine, title: '300–1000 words', text: 'Short enough to finish in one sitting. Long enough to practise a full arc: hook, turn, landing.' },
  { icon: Flame, title: 'Streaks that stick', text: 'Sprint timers, word goals and streak milestones turn "I should write" into "I wrote today".' },
  { icon: BookOpen, title: 'A growing shelf', text: 'Every story is saved, illustrated, shareable. Watch your body of work — and your voice — grow.' },
];

export const PricingIntro = () => (
  <section className="max-w-3xl mx-auto" data-testid="pricing-intro">
    <p className="text-amber-800 text-sm font-medium tracking-wider uppercase mb-3">Why flash fiction</p>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-amber-950 leading-tight tracking-tight mb-6" data-testid="pricing-headline">
      Novelists get better by finishing things.
    </h1>
    <div className="space-y-4 text-lg text-amber-900/85 leading-relaxed font-serif-italic">
      <p>
        A novel takes a year to teach you one lesson. A flash fiction piece teaches you the same lesson before lunch. In under a thousand words you have to open with intent, turn the story, and land it — the whole craft in miniature, repeated daily until it becomes reflex.
      </p>
      <p>
        Flash Fiction is a practice room for writers who want to write regularly, not occasionally. Each day you get a fresh prompt, a timer, a word goal and a quiet page. Finish the piece, save it to your shelf, share it if you dare. Do that for a season and you will notice it in your longer work: tighter openings, braver cuts, endings that resonate.
      </p>
    </div>
    <div className="mt-10 grid sm:grid-cols-2 gap-4">
      {pillars.map(({ icon: Icon, title, text }) => (
        <div key={title} className="bg-white/60 backdrop-blur-sm border border-amber-100 rounded-2xl p-5 flex gap-4">
          <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-amber-800" />
          </div>
          <div>
            <h3 className="font-display font-bold text-amber-950">{title}</h3>
            <p className="text-sm text-amber-900/70 mt-1">{text}</p>
          </div>
        </div>
      ))}
    </div>
    <a href="#plans" className="inline-flex items-center gap-2 mt-8 text-amber-900 font-semibold hover:text-orange-700 group" data-testid="pricing-jump-to-plans">
      See the plans <ArrowDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
    </a>
  </section>
);
